import React, { useContext, useMemo, useState } from 'react';

import { List } from 'antd';

import styled from 'styled-components';

import { UserContext } from '@contexts/UserContext';

import { useWebsites } from '@hooks/API/UseWebsites';

import AddBookDrawer from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/AddBookDrawer';
import BookEditorDrawer from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookEditorDrawer';
import BookItem from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookItem';
import ModelDetailsBooksDrawerFilters from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksDrawerFilters';
import { ModelDetailsBooksContextProvider } from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksContext';
import { useModelDetailsBooksList } from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/useModelDetailsBooksList';

import BooksInPackageList, { splitBooksByPackage } from '@components/BookSelectorForPackages/BooksInPackageList';
import EmptyState from '@components/Gui/EmptyState';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwAddButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import PlayListPreviewDrawer from '@components/MediaDrawer/PlayListPreviewDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PanelWrapper = styled.div`
    display: flex;
    flex-direction: column;
    /* Ad altezza piena testata e filtri restano fermi e scorre solo la lista.
       Sul drawer mobile scorre invece tutto il contenuto. */
    flex: ${(props) => (props.$fillHeight ? '1' : 'none')};
    min-height: 0;

    /* Blocchi bianchi appoggiati sul grigio, come il drawer della modella: la
       separazione la fa lo stacco tra card, non un'etichetta sul bianco. */
    background-color: ${(props) => props.theme.LayoutBodyBackground};
    padding: 1rem;

    .panel-card {
        background-color: ${(props) => props.theme.BackgroundColorLight};
        border-radius: 6px;
        margin-bottom: 1rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .panel-card-head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-bottom: ${(props) => props.theme.DefaultBorder};
    }

    .panel-card-title {
        font-size: 0.8rem;
        font-weight: 500;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: ${(props) => props.theme.TextColor};
    }

    /* new package e new book stanno nella testata del blocco su cui agiscono */
    .panel-card-actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .panel-card-body {
        padding: 1rem;
    }

    /* I filtri non sono un blocco a sé: qualificano la lista che hanno sotto. */
    .panel-card-filters {
        padding: 0.75rem 1rem;
        border-bottom: ${(props) => props.theme.DefaultBorder};
    }

    /* Ad altezza piena è questo il blocco che si prende lo spazio avanzato: dentro,
       a scorrere è solo la lista. */
    .panel-card-books {
        display: flex;
        flex-direction: column;
        flex: ${(props) => (props.$fillHeight ? '1' : 'none')};
        min-height: 0;
    }

    /* Le righe book hanno già il loro padding: qui serve solo lo stacco verticale. */
    .panel-in-package {
        padding: 0.5rem 0;
        min-height: 0;
    }

    .panel-list {
        flex: ${(props) => (props.$fillHeight ? '1' : 'none')};
        min-height: 0;
        overflow-y: ${(props) => (props.$fillHeight ? 'auto' : 'visible')};
        overscroll-behavior: contain;

        /* Barra sottile al posto di quella di sistema, come i contenitori scorrevoli
           di ApplicationFormPalette e ModelDetailsHeaderFeatures */
        scrollbar-width: thin;
        scrollbar-color: ${(props) => props.theme.BorderColor} transparent;

        &::-webkit-scrollbar {
            width: 5px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: ${(props) => props.theme.BorderColor};
            border-radius: 3px;
        }

        &:hover::-webkit-scrollbar-thumb {
            background-color: ${(props) => props.theme.TextColorMuted};
        }
    }

    .panel-loading {
        display: flex;
        justify-content: center;
        padding: 2rem 0;
    }
`;

/**
 * Corpo comune del selettore di book per un package: elenco di quelli già dentro, filtri,
 * elenco di quelli da aggiungere. Non sa niente di package e gruppi — la testata la decide
 * il contenitore e le due callback arrivano già legate alla destinazione giusta.
 *
 * Con headerOnly resta solo la card in alto: la usa chi ci mette dentro il form di
 * creazione package, dove le liste parlerebbero di una destinazione che sta per cambiare.
 */
const BookSelectorPanel = ({
    addedPlaylists,
    contactID,
    destinationLabel = 'adding books to',
    fillHeight = true,
    header,
    headerActions,
    headerOnly = false,
    onAddBook,
    onRemoveBook,
    packageGroup,
    packageName,
}) => {
    const { currentUser } = useContext(UserContext);
    const { data: websiteData } = useWebsites();

    const [bookPreview, setBookPreview] = useState(null);
    const [isAddBookDrawer, setIsAddBookDrawer] = useState(false);
    const [bookToEdit, setBookToEdit] = useState(null);

    const {
        booksSort,
        displayedBooks,
        playlistCharacteristicFilter,
        playlists,
        setBooksSort,
        setPlaylistCharacteristicFilter,
    } = useModelDetailsBooksList({
        contactID,
        currentUserId: currentUser.UserID,
        websiteData: websiteData || [],
        packageSectionActive: true, // esclude i book senza media, come nello scope package
        shouldPaginate: false, // la lista scorre, niente paginazione
        allowManualSort: true,
        enableDrawerFilters: true,
        enableCharacteristicFilter: true,
    });

    // Quello che è già dentro non risente dei filtri: è un elenco di controllo, non
    // qualcosa da cercare. I filtri servono a trovare cosa aggiungere.
    const { booksInPackage, booksToAdd } = useMemo(
        () => splitBooksByPackage(playlists, displayedBooks, addedPlaylists),
        [playlists, displayedBooks, addedPlaylists]
    );

    const handleNewBook = () => {
        setIsAddBookDrawer(true);
    };

    return (
        <>
            <PanelWrapper $fillHeight={fillHeight}>
                {/* Stesso context dello scope package: la riga è il BookItem del books drawer */}
                <ModelDetailsBooksContextProvider
                    readOnly
                    scope="package"
                    addedPlaylists={addedPlaylists}
                    contactID={contactID}
                    packageGroup={packageGroup}
                    packageName={packageName}
                    websiteData={websiteData}
                    onAddBookToPackage={onAddBook}
                    onRemoveBookFromPackage={onRemoveBook}
                    onOpenBookPreviewDrawer={setBookPreview}
                >
                    <div className="panel-card">
                        <div className="panel-card-head">
                            <NwIcon icon={byPrefixAndName.fasl['book-arrow-right']} />
                            <span className="panel-card-title">{destinationLabel}</span>
                            {headerActions && <div className="panel-card-actions">{headerActions}</div>}
                        </div>
                        <div className="panel-card-body">{header}</div>
                    </div>
                    {!headerOnly && (
                        <>
                            {booksInPackage.length > 0 && (
                                <div className="panel-card">
                                    <div className="panel-card-head">
                                        <NwIcon icon={byPrefixAndName.fasl['circle-check']} />
                                        <span className="panel-card-title">in this package</span>
                                    </div>
                                    <div className="panel-in-package">
                                        <BooksInPackageList books={booksInPackage} scrollable={fillHeight} />
                                    </div>
                                </div>
                            )}
                            <div className="panel-card panel-card-books">
                                <div className="panel-card-head">
                                    <NwIcon icon={byPrefixAndName.fasl['circle-plus']} />
                                    <span className="panel-card-title">available books</span>
                                    <div className="panel-card-actions">
                                        <NwAddButton ghost size="small" label="new book" onClick={handleNewBook} />
                                    </div>
                                </div>
                                <div className="panel-card-filters">
                                    <ModelDetailsBooksDrawerFilters
                                        compact
                                        booksSort={booksSort}
                                        onBooksSortChange={setBooksSort}
                                        onPlaylistCharacteristicFilterChange={setPlaylistCharacteristicFilter}
                                        playlistCharacteristicFilter={playlistCharacteristicFilter}
                                        showOnlyMine={false}
                                        showTags={false}
                                    />
                                </div>
                                {!playlists ? (
                                    <div className="panel-loading">
                                        <MiniLoading />
                                    </div>
                                ) : booksToAdd.length > 0 ? (
                                    <div className="panel-list">
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={booksToAdd}
                                            renderItem={(book) => <BookItem book={book} />}
                                        />
                                    </div>
                                ) : (
                                    <EmptyState message={booksInPackage.length > 0 ? 'no other books' : 'no books'} />
                                )}
                            </div>
                        </>
                    )}
                </ModelDetailsBooksContextProvider>
            </PanelWrapper>
            {bookPreview && (
                <PlayListPreviewDrawer
                    playlist={bookPreview}
                    contactID={contactID}
                    onClose={() => setBookPreview(null)}
                />
            )}
            {isAddBookDrawer && (
                <AddBookDrawer
                    contactID={contactID}
                    onClose={() => setIsAddBookDrawer(false)}
                    onCreated={(book) => {
                        // Un book creato da qui è creato per finire nel package: niente
                        // passaggio manuale in più nella lista appena aperta l'editor.
                        onAddBook(book);
                        setBookToEdit(book.ID);
                        setIsAddBookDrawer(false);
                    }}
                />
            )}
            {bookToEdit && (
                <BookEditorDrawer bookID={bookToEdit} contactID={contactID} onClose={() => setBookToEdit(null)} />
            )}
        </>
    );
};

export default BookSelectorPanel;
